// client/src/utils/emailSearch.js
//
// The email lists' search + loading model, shared by the Inbox and Sent pages
// (plans/plan_email_search_2026_10_14.md — the display state model, route R5
// "Route B").
//
// ONE request key is derived from what the screen shows (search text, view,
// mailbox, page) and ONE `refresh()` re-runs it — every fetch goes through it.
// Answers carry the key they were asked for, a counter drops all but the newest
// request's answer, and the page renders an answer only when its key is the key
// it wants NOW. So an old or slow answer can never show under the wrong text,
// view or page, and an action taken on a search result re-runs the search.

import { useCallback, useEffect, useRef, useState } from "react";

// The server's numbers (server/utils/emailLists.js) — pinned equal by a test.
export const SEARCH_MIN_CHARS = 3;
export const SEARCH_CAP = 200;
export const PAGE_SIZE = 50;
export const SEARCH_DEBOUNCE_MS = 500;

/** none (no text), short (1–2 letters), or search (3+, trimmed). */
export function emailSearchMode(raw) {
  const term = typeof raw === "string" ? raw.trim() : "";
  if (!term) return { mode: "none", term: null };
  if (term.length < SEARCH_MIN_CHARS) return { mode: "short", term: null };
  return { mode: "search", term };
}

/** The count line(s) for a search answer: one line, or the two cap lines. */
export function searchCountLines(totalDoc) {
  const n = Number(totalDoc) || 0;
  if (n > SEARCH_CAP) {
    return [
      `${n.toLocaleString("en-US")} matches — showing the newest ${SEARCH_CAP}.`,
      "Type more to narrow it.",
    ];
  }
  return [`${n} ${n === 1 ? "Email" : "Emails"}`];
}

function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

const keyOf = (term, view, mailbox, page) =>
  JSON.stringify(term ? { term, view, mailbox } : { view, mailbox, page });

/**
 * @param {object} p
 * @param {string} p.text      the search box text ("" when the box is closed)
 * @param {*}      p.view      what decides the list besides the mailbox (Inbox: archived?)
 * @param {*}      p.mailbox   which mailbox (my / shared / all)
 * @param {number} p.page      the normal list's page (not part of a search key)
 * @param {(args) => Promise<{emails, totalDoc, error}>} p.runNormal
 * @param {(args) => Promise<{emails, totalDoc, error}>} p.runSearch
 * @returns {{
 *   mode: "none"|"short"|"search",
 *   state: "short"|"pending"|"answered"|"failed",
 *   answer: null|{emails: Array, totalDoc: number},
 *   lastNormal: null|{key: string, view: *, mailbox: *, emails: Array, totalDoc: number, page: number},
 *   lastNormalIsCurrent: boolean,
 *   lastNormalSameList: boolean,
 *   refresh: () => void,
 * }}
 */
export function useEmailList({ text, view, mailbox, page, runNormal, runSearch }) {
  const { mode, term } = emailSearchMode(text);
  const debounced = useDebounced(term, SEARCH_DEBOUNCE_MS);
  const [nonce, setNonce] = useState(0);
  const [answer, setAnswer] = useState(null); // { key, emails, totalDoc, failed }
  const [inflightKey, setInflightKey] = useState(null);
  const [lastNormal, setLastNormal] = useState(null);
  const seq = useRef(0);
  // The runners change every render; the effect must call the current ones.
  const runners = useRef({ runNormal, runSearch });
  runners.current = { runNormal, runSearch };

  const displayKey = mode === "short" ? null : keyOf(term, view, mailbox, page);
  const requestKey =
    mode === "none" ? displayKey : mode === "search" && debounced === term ? displayKey : null;

  useEffect(() => {
    const mine = ++seq.current;
    if (!requestKey) {
      setInflightKey(null);
      return;
    }
    setInflightKey(requestKey);
    const args = JSON.parse(requestKey);
    const run = args.term ? runners.current.runSearch : runners.current.runNormal;
    Promise.resolve()
      .then(() => run(args))
      .then(
        (r) => r,
        (error) => ({ error })
      )
      .then((r) => {
        if (mine !== seq.current) return;
        const failed = !!r?.error || !Array.isArray(r?.emails);
        const next = {
          key: requestKey,
          emails: failed ? [] : r.emails,
          totalDoc: failed ? 0 : Number(r.totalDoc) || 0,
          failed,
        };
        setAnswer(next);
        setInflightKey(null);
        if (!args.term && !failed) {
          setLastNormal({
            key: requestKey,
            view: args.view,
            mailbox: args.mailbox,
            emails: next.emails,
            totalDoc: next.totalDoc,
            page: args.page,
          });
        }
      });
  }, [requestKey, nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  let state;
  if (mode === "short") state = "short";
  else if (answer && answer.key === displayKey && !(answer.failed && inflightKey === displayKey)) {
    state = answer.failed ? "failed" : "answered";
  } else state = "pending";

  return {
    mode,
    state,
    answer: state === "answered" || state === "failed" ? answer : null,
    lastNormal,
    // The last normal rows are THIS screen's (same view, mailbox and page) —
    // only then may a failed normal fetch keep showing them (D27). A failed
    // load of any other screen shows "Emails didn't load" instead.
    lastNormalIsCurrent: !!lastNormal && lastNormal.key === displayKey,
    // ...and the same LIST (view and mailbox), any page: its count and pager
    // stay while another page loads or after it fails, so the user can page
    // back from a page that will not load — never under another view's label.
    lastNormalSameList:
      !!lastNormal && mode === "none" && lastNormal.view === view && lastNormal.mailbox === mailbox,
    refresh,
  };
}
